import HoverTooltip from '@deltek/specification-client/core/Feedbacks/HoverTooltip'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { Button, CardContent, makeStyles, Theme } from '@material-ui/core'
import Avatar from '@material-ui/core/Avatar'
import CardHeader from '@material-ui/core/CardHeader'
import Typography from '@material-ui/core/Typography'
import clsx from 'clsx'
import format from 'date-fns/format'
import isToday from 'date-fns/isToday'
import { TrackChangeStatus } from 'models/changes'
import React, { useCallback, useEffect } from 'react'
import { sanitizeAndDecodeValue } from '../PropertyEditor/PropertyEditorEditableText'
import { CommentButtons } from './CommentButtons'
import { CommentSection } from './CommentSection'
import useBetaWorkspaceToggle from './hooks/useBetaWorkspaceToggle'
import { CardContentProps, EditableCommentProps } from './types/heightManagement.types'

export interface ChangesCardContentStyleProps {
    isBetaWorkspaceToggleEnabled?: boolean
    isTextOverflowed?: boolean
    actionElement?: JSX.Element & React.ReactNode
}

const useCardHeaderTextStyles = makeStyles((theme: Theme) => ({
    cardHeaderText: ({
        isBetaWorkspaceToggleEnabled,
        isTextOverflowed,
        actionElement,
    }: ChangesCardContentStyleProps) => {
        const isCommentActionButton =
            actionElement?.props?.['data-testid'] === 'commentActionButton'

        const fontSize = pxToRem(isBetaWorkspaceToggleEnabled ? 10 : 13)
        const fontWeight = isBetaWorkspaceToggleEnabled ? 400 : 'inherit'
        const color = isBetaWorkspaceToggleEnabled
            ? '#343434'
            : theme.palette.common.black

        const maxWidth = pxToRem(
            isTextOverflowed ||
                isCommentActionButton ||
                isBetaWorkspaceToggleEnabled
                ? 220
                : 115
        )

        return {
            fontSize,
            fontWeight,
            lineHeight: pxToRem(15),
            color,
            maxWidth,
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
        }
    },
}))

const useCardHeaderTitleStyles = makeStyles((theme: Theme) => ({
    cardHeaderTitle: ({
        isBetaWorkspaceToggleEnabled,
        isTextOverflowed,
        actionElement,
    }: ChangesCardContentStyleProps) => {
        const isCommentActionButton =
            actionElement?.props?.['data-testid'] === 'commentActionButton'

        const maxWidth = pxToRem(
            isCommentActionButton
                ? isBetaWorkspaceToggleEnabled
                    ? 190
                    : 115
                : actionElement
                ? isBetaWorkspaceToggleEnabled
                    ? 160
                    : 115
                : isBetaWorkspaceToggleEnabled && isTextOverflowed
                ? 200
                : 115
        )

        return {
            fontSize: isBetaWorkspaceToggleEnabled ? pxToRem(12) : pxToRem(13),
            fontWeight: isBetaWorkspaceToggleEnabled ? 600 : 400,
            color: isBetaWorkspaceToggleEnabled ? '#343434' : '#000',
            maxWidth,
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            display: 'inline-block',
        }
    },
}))

const useStyles = makeStyles((theme: Theme) => ({
    cardButton: {
        padding: 0,
        color: '#d3d3d3',
    },
    cardIconButton: {
        '&:hover': {
            color: '#0098D6',
            background: 'none',
        },
    },
    avatar: {
        backgroundColor: '#007aac',
        padding: theme.spacing(0, 0.125, 0, 0),
    },
    changesCard: {
        minWidth: pxToRem(234),
        maxWidth: pxToRem(234),
        margin: theme.spacing(2, 0),
        borderRadius: pxToRem(4),
        cursor: 'pointer',
        boxShadow: (props: ChangesCardContentStyleProps) =>
            !props.isBetaWorkspaceToggleEnabled
                ? '0px 1px 3px 0px #A9A9A8'
                : 'none !important',
    },
    cardContent: {
        minWidth: pxToRem(234),
        maxWidth: (props: ChangesCardContentStyleProps) =>
            props.isBetaWorkspaceToggleEnabled ? pxToRem(300) : pxToRem(234),
        padding: theme.spacing(0, 2),
        marginBottom: theme.spacing(1),
        '&:last-child': {
            paddingBottom: 0,
        },
    },
    text: {
        wordBreak: 'break-word',
        fontSize: (props: ChangesCardContentStyleProps) =>
            props.isBetaWorkspaceToggleEnabled ? pxToRem(12) : pxToRem(13),
        display: 'inline',
        fontWeight: 400,
        marginLeft: theme.spacing(0.625),
    },
    textHeight: {
        maxHeight: pxToRem(60),
    },
    showButton: {
        padding: 0,
        fontSize: pxToRem(13),
        color: (props: ChangesCardContentStyleProps) =>
            props.isBetaWorkspaceToggleEnabled ? '#314959' : '#A78966',
        textDecoration: 'underline',
    },
    cardHeader: {
        width: '100%',
        padding: theme.spacing(2, 0, 2, 1.5),
        marginRight: theme.spacing(1.5),
    },
    cardHeaderAvatar: {
        marginRight: theme.spacing(1.25),
    },
    cardHeaderAction: (props: ChangesCardContentStyleProps) => ({
        marginLeft: props.isBetaWorkspaceToggleEnabled
            ? '0 !important'
            : theme.spacing(0.5),
        marginRight: props.isBetaWorkspaceToggleEnabled
            ? '0 !important'
            : 'unset',
    }),
    changeTypeText: {
        display: 'inline',
        fontSize: (props: ChangesCardContentStyleProps) =>
            props.isBetaWorkspaceToggleEnabled ? pxToRem(12) : pxToRem(13),
        fontWeight: 700,
    },
    iconContainer: {
        paddingTop: theme.spacing(1),
    },
    acceptButtonContainer: {
        marginRight: theme.spacing(0.375),
    },
    highlight: {
        border: '1px solid rgba(0, 152, 214, 0.6)',
        boxShadow: '0px 1px 4px rgba(0, 152, 214, 0.65)',
    },
    border: {
        borderTop: '1px solid #C9C9C9',
    },
    acceptedText: {
        color: (props: ChangesCardContentStyleProps) =>
            props.isBetaWorkspaceToggleEnabled ? '#006400' : '#33B55B',
        fontStyle: 'italic',
    },
    rejectedText: {
        color: '#FF6060',
        fontStyle: 'italic',
    },
    deleteWarningOverlay: {
        background: 'rgba(0,0,0,0.77)',
        position: 'absolute',
        minHeight: pxToRem(106),
        height: 'inherit',
        display: (props: ChangesCardContentStyleProps) =>
            props.isBetaWorkspaceToggleEnabled ? 'grid' : 'inherit',
    },
    deleteWarningText: {
        color: theme.palette.common.white,
        fontSize: pxToRem(15),
    },
    cardContentGrid: {
        position: 'relative',
        height: '100%',
    },
}))

export interface ChangesCardContentProps extends CardContentProps {
    actionElement?: JSX.Element & React.ReactNode
    changeStatus?: TrackChangeStatus
}

export const ChangesCardContent = React.memo(
    ({
        imageUrl,
        avatarLetter,
        userName,
        dateTime,
        text,
        prefix,
        actionElement,
        deleteCommentCallback,
        hideDeleteWarning,
        isDeleteWarningDisplayed,
        indexReference,
        isEditable,
        editableProps,
        isSelected,
        changeStatus,
        hideHeader,
        contentItemElementId,
        onHeightChange,
    }: ChangesCardContentProps) => {
        const isBetaWorkspaceToggleEnabled = useBetaWorkspaceToggle()
        const userNameTextRef = React.useRef<HTMLSpanElement | null>(null)
        const [isTextOverflowed, setIsTextOverflowed] = React.useState(false)
        const cardHeaderTextClasses = useCardHeaderTextStyles({
            isBetaWorkspaceToggleEnabled,
            isTextOverflowed,
            actionElement,
        })
        const cardHeaderTitleClasses = useCardHeaderTitleStyles({
            isBetaWorkspaceToggleEnabled,
            isTextOverflowed,
            actionElement,
        })
        const classes = useStyles({
            isBetaWorkspaceToggleEnabled,
            isTextOverflowed,
            actionElement,
        })

        const contentRef = React.useRef<any | undefined | null>()
        const [
            isShowMoreButtonDisplayed,
            setIsShowMoreButtonDisplayed,
        ] = React.useState<boolean | undefined>()

        const handleShowMore = useCallback(() => {
            setIsShowMoreButtonDisplayed((prevState) => !prevState)
            // Trigger height remeasurement when content expands/collapses
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    onHeightChange?.()
                })
            })
        }, [onHeightChange])

        const handleDelete = useCallback(() => {
            deleteCommentCallback?.(indexReference as number)
        }, [deleteCommentCallback, indexReference])

        const handleCancel = useCallback(() => {
            hideDeleteWarning?.()
        }, [hideDeleteWarning])

        const sanitizedText = React.useMemo(
            () => sanitizeAndDecodeValue(text),
            [text]
        )

        useEffect(() => {
            if (contentRef && contentRef?.current) {
                if (isShowMoreButtonDisplayed === undefined) {
                    if (
                        contentRef?.current?.scrollHeight >
                            contentRef?.current?.offsetHeight ||
                        contentRef?.current?.scrollHeight > 60
                    ) {
                        setIsShowMoreButtonDisplayed(true)
                    } else {
                        if (isShowMoreButtonDisplayed) {
                            setIsShowMoreButtonDisplayed(false)
                        }
                    }
                } else if (
                    isShowMoreButtonDisplayed !== undefined &&
                    !!contentItemElementId
                ) {
                    if (
                        !isSelected &&
                        isShowMoreButtonDisplayed !== undefined
                    ) {
                        if (
                            contentRef?.current?.scrollHeight <
                                contentRef?.current?.offsetHeight ||
                            contentRef?.current?.scrollHeight <= 60
                        ) {
                            setIsShowMoreButtonDisplayed(undefined)
                        } else {
                            setIsShowMoreButtonDisplayed(true)
                        }
                    } else if (
                        contentRef?.current?.scrollHeight <
                            contentRef?.current?.offsetHeight ||
                        contentRef?.current?.scrollHeight <= 60
                    ) {
                        setIsShowMoreButtonDisplayed(undefined)
                    }
                }
            }
        }, [
            contentRef,
            isShowMoreButtonDisplayed,
            text,
            isEditable,
            isSelected,
        ]) //eslint-disable-line

        React.useEffect(() => {
            const element = userNameTextRef.current
            if (element) {
                setIsTextOverflowed(element.scrollWidth > element.offsetWidth)
            }
        }, [userName])

        const renderTitle = React.useCallback(
            (userName: string) => {
                return (
                    <HoverTooltip
                        text={isTextOverflowed ? userName : undefined}
                    >
                        <Typography
                            ref={userNameTextRef}
                            className={cardHeaderTitleClasses.cardHeaderTitle}
                        >
                            {userName}
                        </Typography>
                    </HoverTooltip>
                )
            },
            [isTextOverflowed]
        )

        const formattedDate = React.useMemo(() => {
            const date = new Date(dateTime)
            const isTodayFlag = isToday(date)
            return {
                formattedDate: format(date, 'MM/dd/yyyy'),
                formattedTime: format(date, 'h:mm a'),
                isTodayFlag,
            }
        }, [dateTime])

        const showMoreButtonText = `Show ${
            isShowMoreButtonDisplayed
                ? 'More'
                : isShowMoreButtonDisplayed !== undefined && 'Less'
        }`

        return (
            <GridContainer
                className={clsx(classes.cardContentGrid, {
                    [classes.border]:
                        !!changeStatus &&
                        (changeStatus === TrackChangeStatus.APPROVED ||
                            changeStatus === TrackChangeStatus.REJECTED),
                })}
            >
                {!hideHeader && (
                    <CardHeader
                        avatar={
                            <Avatar
                                className={classes.avatar}
                                data-testid="cardAvatar"
                                src={imageUrl}
                                alt={`${userName} avatar`}
                            >
                                {avatarLetter}
                            </Avatar>
                        }
                        action={actionElement}
                        title={renderTitle(userName)}
                        subheader={
                            <>
                                <Typography
                                    className={
                                        cardHeaderTextClasses.cardHeaderText
                                    }
                                    data-testid="timeDetails"
                                >
                                    {formattedDate.formattedTime}
                                </Typography>{' '}
                                <Typography
                                    className={
                                        cardHeaderTextClasses.cardHeaderText
                                    }
                                >
                                    {formattedDate.isTodayFlag
                                        ? 'Today'
                                        : formattedDate.formattedDate}
                                </Typography>
                            </>
                        }
                        classes={{
                            title: cardHeaderTextClasses.cardHeaderText,
                            root: classes.cardHeader,
                            avatar: classes.cardHeaderAvatar,
                            action: classes.cardHeaderAction,
                        }}
                    />
                )}
                {!isEditable ? (
                    <>
                        {(prefix || text) && (
                            <CardContent
                                className={classes.cardContent}
                                data-testid="cardContent"
                            >
                                <div
                                    ref={contentRef}
                                    style={{ overflow: 'hidden' }}
                                    className={clsx(
                                        isShowMoreButtonDisplayed &&
                                            classes.textHeight
                                    )}
                                >
                                    {prefix && (
                                        <Typography
                                            component="span"
                                            className={classes.changeTypeText}
                                        >
                                            {prefix}:
                                        </Typography>
                                    )}
                                    <Typography
                                        className={clsx(classes.text, {
                                            [classes.acceptedText]:
                                                !!changeStatus &&
                                                changeStatus ===
                                                    TrackChangeStatus.APPROVED,
                                            [classes.rejectedText]:
                                                !!changeStatus &&
                                                changeStatus ===
                                                    TrackChangeStatus.REJECTED,
                                        })}
                                        component="span"
                                    >
                                        {sanitizedText}
                                    </Typography>
                                </div>
                                {(isShowMoreButtonDisplayed ||
                                    isShowMoreButtonDisplayed !==
                                        undefined) && (
                                    <Button
                                        onClick={handleShowMore}
                                        className={classes.showButton}
                                        disableRipple
                                        disableFocusRipple
                                    >
                                        {showMoreButtonText}
                                    </Button>
                                )}
                            </CardContent>
                        )}
                        {isDeleteWarningDisplayed && (
                            <GridContainer
                                justifyContent="center"
                                alignContent="center"
                                className={classes.deleteWarningOverlay}
                                data-testid="deleteWarningOverlay"
                            >
                                <GridItem>
                                    <Typography
                                        component="span"
                                        className={classes.deleteWarningText}
                                    >
                                        Delete this comment?
                                    </Typography>
                                </GridItem>
                                <GridItem>
                                    <CommentButtons
                                        leftButtonText="Delete"
                                        leftButtonActionCallback={handleDelete}
                                        rightButtonText="Cancel"
                                        rightButtonActionCallback={handleCancel}
                                        areButtonsDisabled={false}
                                    />
                                </GridItem>
                            </GridContainer>
                        )}
                    </>
                ) : (
                    <CardContent
                        className={classes.cardContent}
                        data-testid="cardContent"
                    >
                        <CommentSection
                            text={sanitizedText}
                            commentId={editableProps?.commentId}
                            trackChangeId={editableProps?.trackChangeId ?? ''}
                            elementId={''}
                            familyId={''}
                            handleEdit={editableProps?.handleEdit}
                            onHeightChange={editableProps?.onHeightChange}
                        />
                    </CardContent>
                )}
            </GridContainer>
        )
    }
)
